import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useFormContext } from 'react-hook-form';
import { fetchSenders } from 'modules/senders/actions';
import { SMSSender } from 'modules/graphql';
import { formatSMSSenderOptions } from 'modules/contactCards/utils';
import { BasicSelectField, InputField } from 'lib/iterable-hook-form';

import { BUTTON_SIZES, BUTTON_VARIANTS, PrimaryButton, SecondaryButton } from 'ui/button';
import { FormField } from 'ui/formField';
import { Tooltip } from 'ui/tooltip';
import { ConfirmationPrompt, ModalBody, ModalConfirmationFooter } from 'ui/modal';
import { ChevronLeft16, Help16 } from 'ui/icons';
import { FocusStyle } from 'ui/tooltip/types';
import { Link } from 'ui/links';

import { DoubleOptInMessageTypeProps } from './types';
import {
  BRAND_NAME_FIELD_LENGTH_LIMIT,
  DOI_DOCUMENTATION_LINK,
  FORM_ERROR_BANNER_CONTENT,
  MESSAGE_TYPE_TEXT,
  MESSAGING_INITIATIVE_FIELD_LENGTH_LIMIT,
  SMS_DISCLAIMER_LINK_FIELD_LENGTH_LIMIT,
} from './constants';
import { StyledLabel } from '../Styled';
import { DoubleOptInPreview } from './DoubleOptInPreview';
import {
  ContentContainer,
  FormContainer,
  StyledErrorBanner,
  StyledModalHeader,
  StyledIconButton,
  DoubleOptInHelpText,
} from './ManageMessageTypeModal.styled';

const getRestrictionText = (text: string, maxLength: number) =>
  `${text.length}/${maxLength} characters`;

const FormFieldLabel = ({ labelText, tooltipText }: { labelText: string; tooltipText: string }) => {
  return (
    <StyledLabel>
      {labelText}
      <Tooltip $focusStyle={FocusStyle.Round} tooltipContent={tooltipText}>
        <Help16 />
      </Tooltip>
    </StyledLabel>
  );
};

const DoubleOptInForm = ({ isEditMode }: { isEditMode: boolean }) => {
  const dispatch = useDispatch();
  const {
    watch,
    formState: {
      errors: {
        messageTypeName: nameError,
        brandName: brandNameError,
        messagingInitiative: messagingInitiativeError,
        smsDisclaimerLink: smsDisclaimerLinkError,
        sender: senderError,
      },
      isSubmitted,
    },
  } = useFormContext();
  const brandName = watch('brandName');
  const messagingInitiative = watch('messagingInitiative');
  const smsDisclaimerLink = watch('smsDisclaimerLink');

  const hasError =
    brandNameError || messagingInitiativeError || smsDisclaimerLinkError || senderError;

  useEffect(() => {
    dispatch(fetchSenders());
  }, []);

  const smsSenders: SMSSender[] = useSelector(
    (state: any) => state?.settings?.senders?.smsSenders || [],
  );

  return (
    <FormContainer>
      {hasError && <StyledErrorBanner>{FORM_ERROR_BANNER_CONTENT}</StyledErrorBanner>}
      {isEditMode && (
        <FormField
          labelText="Name"
          hasError={Boolean(nameError?.message) && isSubmitted}
          errorText={`${nameError?.message}`}
          data-test="double-opt-in-form-field-name"
        >
          <InputField name="messageTypeName" />
        </FormField>
      )}
      <FormField
        labelText={
          <FormFieldLabel
            labelText="Brand name"
            tooltipText="Who the recipient is receiving messages from"
          />
        }
        restrictionText={getRestrictionText(brandName, BRAND_NAME_FIELD_LENGTH_LIMIT)}
        hasError={Boolean(brandNameError?.message) && isSubmitted}
        errorText={`${brandNameError?.message}`}
        data-test="double-opt-in-form-field-brand-name"
      >
        <InputField
          name="brandName"
          maxLength={BRAND_NAME_FIELD_LENGTH_LIMIT}
          placeholder="Example: “Iterable”"
        />
      </FormField>
      <FormField
        labelText={
          <FormFieldLabel
            labelText="Messaging initiative"
            tooltipText="The subject matter the recipient is subscribing to"
          />
        }
        restrictionText={getRestrictionText(
          messagingInitiative,
          MESSAGING_INITIATIVE_FIELD_LENGTH_LIMIT,
        )}
        hasError={Boolean(messagingInitiativeError?.message) && isSubmitted}
        errorText={`${messagingInitiativeError?.message}`}
        data-test="double-opt-in-form-field-messaging-initiative"
      >
        <InputField
          name="messagingInitiative"
          maxLength={MESSAGING_INITIATIVE_FIELD_LENGTH_LIMIT}
          placeholder="Example: “order status updates”"
        />
      </FormField>
      <FormField
        labelText="SMS disclaimer link"
        restrictionText={getRestrictionText(
          smsDisclaimerLink,
          SMS_DISCLAIMER_LINK_FIELD_LENGTH_LIMIT,
        )}
        hasError={Boolean(smsDisclaimerLinkError?.message) && isSubmitted}
        errorText={`${smsDisclaimerLinkError?.message}`}
        data-test="double-opt-in-form-field-sms-disclaimer-link"
      >
        <InputField
          name="smsDisclaimerLink"
          maxLength={SMS_DISCLAIMER_LINK_FIELD_LENGTH_LIMIT}
          placeholder="Example: “https://www.example.com/sms_toc”"
        />
      </FormField>
      <FormField
        labelText="Sender"
        hasError={Boolean(senderError?.message) && isSubmitted}
        errorText={`${senderError?.message}`}
        data-test="double-opt-in-form-field-sender"
      >
        <BasicSelectField
          name="sender"
          options={formatSMSSenderOptions(smsSenders)}
          isFullWidth
          placeholder="Select a sender..."
        />
      </FormField>
      <DoubleOptInHelpText>
        Questions regarding the double opt-in? Please contact your CSM or refer to this{' '}
        <Link href={DOI_DOCUMENTATION_LINK}>documentation</Link>
      </DoubleOptInHelpText>
    </FormContainer>
  );
};

export const ManageDoubleOptInType = ({
  onClose,
  onSubmit,
  isEditMode,
  isUpdateToDoubleOptInMode,
  prevAction,
}: DoubleOptInMessageTypeProps) => {
  const [confirmationPromptType, setConfirmationPromptType] = useState<ConfirmationPrompt>(
    ConfirmationPrompt.NONE,
  );
  const {
    watch,
    handleSubmit,
    formState: { isDirty },
  } = useFormContext();

  const messageTypeName = watch('messageTypeName');

  const getButtonText = () => {
    if (isEditMode) return MESSAGE_TYPE_TEXT.UPDATE_ACTION_BUTTON_DOUBLE_OPT_IN;
    else if (isUpdateToDoubleOptInMode)
      return MESSAGE_TYPE_TEXT.UPDATE_TYPE_TO_DOUBLE_OPT_IN_BUTTON;
    return MESSAGE_TYPE_TEXT.CREATE_ACTION_BUTTON;
  };

  const handleCancel = () => {
    if (isDirty) setConfirmationPromptType(ConfirmationPrompt.DISCARD);
    else onClose();
  };

  return (
    <>
      <StyledModalHeader
        data-with-icon-button={!!prevAction && !isEditMode}
        data-test="message-type-double-opt-in-header"
      >
        {!!prevAction && !isEditMode && (
          <StyledIconButton
            size={BUTTON_SIZES.LARGE}
            variant={BUTTON_VARIANTS.GHOST}
            onClick={prevAction}
          >
            <ChevronLeft16 />
          </StyledIconButton>
        )}
        {messageTypeName}
      </StyledModalHeader>
      <ModalBody>
        <ContentContainer>
          <DoubleOptInForm isEditMode={isEditMode && !isUpdateToDoubleOptInMode} />
          <DoubleOptInPreview />
        </ContentContainer>
      </ModalBody>
      <ModalConfirmationFooter
        promptType={confirmationPromptType}
        onCancel={handleCancel}
        onResume={() => setConfirmationPromptType(ConfirmationPrompt.NONE)}
        onDiscard={onClose}
      >
        {!!prevAction && !isEditMode && (
          <SecondaryButton onClick={prevAction}>Back</SecondaryButton>
        )}
        <PrimaryButton onClick={handleSubmit(onSubmit)}>{getButtonText()}</PrimaryButton>
      </ModalConfirmationFooter>
    </>
  );
};
